import { EmotionCache } from "@emotion/react";
import { Cookies } from "app-cookies";
import { AuthProvider } from "auth";
import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { ResourcesConfig } from "@aws-amplify/core";
import maplibregl from "maplibre-gl";
import { NextPage } from "next";
import { AppProps } from "next/app";
import { NextRouter } from "next/router";
import NProgress from "nprogress";
import {
	useEffect,
	useState,
	createContext,
	useContext,
	useCallback,
} from "react";
import { TagManager } from "tag-manager";
import { DefaultSeo, Toaster } from "ui-components";
import { I18nProvider, QueryProvider, ReactDevTools } from "utils";
import {
	defaultSettings,
	SettingsProvider,
} from "~mangomap-ui/components/core/settings/settings-context";
import { ThemeProvider } from "~mangomap-ui/components/core/theme-provider";
import { LocalizationProvider } from "~mangomap-ui/components/core/localization-provider";
import GlobalStyles from "@mui/material/GlobalStyles";

NProgress.configure({ showSpinner: false });

import "@fontsource/inter/100.css";
import "@fontsource/inter/200.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource/inter/900.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/roboto-mono/300.css";
import "@fontsource/roboto-mono/400.css";
import "~mangomap-ui/styles/global.css";

import "aos/dist/aos.css";
import { routes } from "config/paths";
import "material-symbols/rounded.css";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "maplibre-gl/dist/maplibre-gl.css";

// Configure Amplify
const config: ResourcesConfig = {
	API: {
		GraphQL: {
			endpoint: process.env.API_URL!,
			defaultAuthMode: "apiKey" as const,
			apiKey: process.env.API_KEY!,
		},
	},
	Auth: {
		Cognito: {
			userPoolId: process.env.AUTH_USER_POOL_ID!,
			userPoolClientId: process.env.AUTH_USER_POOL_CLIENT_WEB_ID!,
			signUpVerificationMethod: "code",
		},
	},
};

Amplify.configure(config);

ReactDevTools.init();

export interface CustomAppProps extends AppProps {
	Component: NextPage;
	emotionCache?: EmotionCache;
}

// Customer context for white-label domains
interface CustomerInfo {
	customerDomain: string;
}

const CustomerContext = createContext<CustomerInfo | null>(null);

export const useCustomer = () => {
	const context = useContext(CustomerContext);
	if (context === undefined) {
		throw new Error("useCustomer must be used within a CustomerProvider");
	}
	return context;
};

const loadCogProtocol = async () => {
	const { cogProtocol } = await import("@geomatico/maplibre-cog-protocol");
	maplibregl.addProtocol("cog", cogProtocol);
};
if (typeof window !== "undefined") void loadCogProtocol();

const client = generateClient();

interface ValidateDomainResponse {
	validateDomain: {
		authorized: boolean;
		error?: string;
	};
}

const validateAndSetCustomerInfo = async (
	domain: string,
	setCustomerInfo: (info: CustomerInfo) => void,
	router: NextRouter,
) => {
	try {
		// Skip validation if already on 404 page
		if (router.pathname === "/404") {
			return;
		}

		console.log("Validating domain:", domain);

		const response = (await client.graphql({
			query: `
        query ValidateDomain($domain: String!) {              
          validateDomain(host: $domain) {
            authorized
            error
          }
        }
      `,
			variables: { domain },
			authMode: "apiKey",
		})) as GraphQLResult<ValidateDomainResponse>;

		const data = response.data?.validateDomain;
		if (data?.authorized) {
			setCustomerInfo({
				customerDomain: domain,
			});
		} else {
			// Use router.replace to avoid adding to history stack
			void router.replace("/404");
		}
	} catch (error) {
		console.error("Failed to validate domain:", error);
		// Use router.replace for errors as well
		void router.replace("/404");
	}
};

// If any route has been moved to Devias theme, remove it from this list
const OLD_ROUTES = ["/franchise"];

const App = ({
	Component,
	pageProps,
	// emotionCache = clientSideEmotionCache,
	router,
}: CustomAppProps) => {
	const isOldRoute = OLD_ROUTES.includes(router.pathname);
	const isFranchise = router.pathname === routes.franchise;
	const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
	const [hasValidated, setHasValidated] = useState(false);

	useNprogress(router);

	const validateDomain = useCallback(() => {
		if (typeof window !== "undefined" && !hasValidated) {
			console.log("Window exists");

			// Skip domain validation on 404 page to prevent redirect loops
			if (router.pathname === "/404") {
				console.log("404 page detected, skipping domain validation");
				setHasValidated(true);
				return;
			}

			// Check if we're on a customer domain
			const hostname = window.location.hostname;
			const mainDomain = process.env.ROUTER_DOMAIN_NAME;
			if (hostname === "localhost" || hostname.startsWith("localhost:")) {
				console.log("Localhost detected, skipping");
				setHasValidated(true);
				return;
			}

			// Handle main domain
			if (hostname === mainDomain) {
				console.log("Main domain detected", hostname);
				setCustomerInfo({
					customerDomain: hostname,
				});
				setHasValidated(true);
				return;
			}

			// Validate customer domains
			if (mainDomain && hostname !== mainDomain) {
				console.log("Customer domain detected, validating...");
				void validateAndSetCustomerInfo(hostname, setCustomerInfo, router);
				setHasValidated(true);
			}
		}
	}, [hasValidated, router]);

	// Extract customer info from headers (set by middleware)
	useEffect(() => {
		validateDomain();
	}, [validateDomain]);

	return (
		<>
			<DefaultSeo />
			<TagManager
				gaId={
					isFranchise
						? process.env.FRANCHISE_GA_MEASUREMENT_ID!
						: process.env.GIS_GA_MEASUREMENT_ID!
				}
				gtmId={
					isFranchise
						? process.env.FRANCHISE_GOOGLE_TAG_MANAGER_ID!
						: process.env.GIS_GOOGLE_TAG_MANAGER_ID!
				}
			/>
			<I18nProvider language="en">
				<LocalizationProvider>
					<QueryProvider>
						<AuthProvider>
							<CustomerContext.Provider value={customerInfo}>
								{isOldRoute ? (
									<Component
										{...pageProps}
										customerInfo={customerInfo}
									/>
								) : (
									<SettingsProvider settings={defaultSettings}>
										<ThemeProvider>
											<AppGlobalStyles />
											<Cookies />
											<Component
												{...pageProps}
												customerInfo={customerInfo}
											/>
											<Toaster position="bottom-right" />
										</ThemeProvider>
									</SettingsProvider>
								)}
							</CustomerContext.Provider>
						</AuthProvider>
					</QueryProvider>
				</LocalizationProvider>
			</I18nProvider>
		</>
	);
};

export default App;

const useNprogress = (router) => {
	useEffect(() => {
		const handleRouteStart = () => NProgress.start();
		const handleRouteDone = () => NProgress.done();

		router.events.on("routeChangeStart", handleRouteStart);
		router.events.on("routeChangeComplete", handleRouteDone);
		router.events.on("routeChangeError", handleRouteDone);

		return () => {
			// Make sure to remove the event handler on unmount!
			router.events.off("routeChangeStart", handleRouteStart);
			router.events.off("routeChangeComplete", handleRouteDone);
			router.events.off("routeChangeError", handleRouteDone);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

const AppGlobalStyles = () => (
	<GlobalStyles
		styles={(t) => `
			/* Remove is recharts is not used */
			.recharts-cartesian-axis-tick-value {
				color: var(--mui-palette-text-secondary);
				font-size: var(--fontSize-xs);
			}

			.recharts-cartesian-grid line {
				stroke: var(--mui-palette-divider);
			}

			.recharts-pie path:focus {
				outline: none;
			}

			/* Remove if react-simple-maps is not used */
			.rsm-geographies path:focus {
				outline: none;
			}

			.popup-content > .maplibregl-popup-content {
				padding: 0px;
			}

			/* Remove if fullcalendar is not used */
			.fc {
				--fc-bg-event-opacity: 1;
				--fc-border-color: var(--mui-palette-divider);
				--fc-daygrid-event-dot-width: 10px;
				--fc-event-bg-color: var(--mui-palette-background-paper);
				--fc-event-border-color: var(--mui-palette-divider);
				--fc-event-text-color: var(--mui-palette-primary-contrastTex);
				--fc-list-event-hover-bg-color: var(--mui-palette-background-default);
				--fc-neutral-bg-color: var(--mui-palette-background-default);
				--fc-page-bg-color: var(--mui-palette-background-default);
				--fc-today-bg-color: var(--mui-palette-background-level1);
				color: var(--mui-palette-text-primary);
			}

			.fc .fc-license-message {
				display: none;
			}

			.fc .fc-list {
				border-color: transparent;
			}

			.fc .fc-scrollgrid {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-header > th {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-body > td {
				border-color: transparent;
			}

			.fc .fc-col-header-cell:first-of-type {
				border-left-color: transparent;
			}

			.fc .fc-col-header-cell:last-of-type {
				border-right-color: transparent;
			}

			.fc .fc-col-header-cell-cushion {
				font-size: 0.75rem;
				font-weight: 600;
				letter-spacing: 0.5px;
				line-height: 2.5;
				padding: 8px;
				text-transform: uppercase;
			}

			.fc .fc-daygrid-day-frame {
				padding: 12px;
			}

			.fc .fc-day-other .fc-daygrid-day-top {
				color: var(--mui-palette-text-secondary);
			}

			.fc .fc-event {
				background: transparent;
				border: none;
				padding: 0;
			}

			/* Variables */
			:root {
				--fontSize-xs: 0.75rem;
				--fontSize-sm: 0.875rem;
				--fontSize-md: 1rem;
				--fontSize-lg: 1.125rem;
				--fontSize-xl: 1.25rem;
				--fontSize-2xl: 1.5rem;

				--icon-fontSize-sm: 1rem;
				--icon-fontSize-md: 1.25rem;
				--icon-fontSize-lg: 1.5rem;

				/* Remember to keep in sync with theme breakpoints */
				--maxWidth-xs: 0;
				--maxWidth-sm: 600px;
				--maxWidth-md: 900px;
				--maxWidth-lg: 1200px;
				--maxWidth-xl: 1440px;
			}

			*:focus-visible {
				outline: 2px solid var(--mui-palette-primary-main);
			}

			html {
				height: 100%;
			}

			body {
				height: 100%;
			}

			#nprogress {
				pointer-events: none;
			}

			#nprogress .bar {
				background: ${t.palette.primary.main};

				position: fixed;
				z-index: 1101;
				top: 0;
				left: 0;

				width: 100%;
				height: 2px;
			}

			/* Fancy blur effect */
			#nprogress .peg {
				display: block;
				position: absolute;
				right: 0px;
				width: 100px;
				height: 100%;
				box-shadow: 0 0 10px ${t.palette.primary.main}, 0 0 5px ${t.palette.primary.main};
				opacity: 1;

				-webkit-transform: rotate(3deg) translate(0px, -4px);
				-ms-transform: rotate(3deg) translate(0px, -4px);
				transform: rotate(3deg) translate(0px, -4px);
			}

			/* Remove these to get rid of the spinner */
			#nprogress .spinner {
				display: block;
				position: fixed;
				z-index: 1101;
				top: 15px;
				right: 15px;
			}

			#nprogress .spinner-icon {
				width: 18px;
				height: 18px;
				box-sizing: border-box;

				border: solid 2px transparent;
				border-top-color: ${t.palette.primary.main};
				border-left-color: ${t.palette.primary.main};
				border-radius: 50%;

				-webkit-animation: nprogress-spinner 400ms linear infinite;
				animation: nprogress-spinner 400ms linear infinite;
			}

			.nprogress-custom-parent {
				overflow: hidden;
				position: relative;
			}

			.nprogress-custom-parent #nprogress .spinner,
			.nprogress-custom-parent #nprogress .bar {
				position: absolute;
			}

			@-webkit-keyframes nprogress-spinner {
				0% {
					-webkit-transform: rotate(0deg);
				}
				100% {
					-webkit-transform: rotate(360deg);
				}
			}
			@keyframes nprogress-spinner {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}
		`}
	/>
);
